import React, { useState, useEffect } from 'react';
import { Package, DollarSign, Clock, Truck, CheckCircle, ChevronDown, ChevronUp } from 'lucide-react';
import { Button } from '../components/ui/button';
import { toast } from 'sonner';
import axios from 'axios';

const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api`;

const AdminOrders = () => {
  const [orders, setOrders] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all');
  const [expandedOrder, setExpandedOrder] = useState(null);

  useEffect(() => {
    fetchOrders();
    fetchStats();
  }, [filter]);

  const fetchOrders = async () => {
    try {
      const url = filter === 'all' 
        ? `${API_URL}/shop/admin/orders`
        : `${API_URL}/shop/admin/orders?status=${filter}`;
      const response = await axios.get(url);
      setOrders(response.data);
    } catch (error) {
      console.error('Failed to fetch orders:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchStats = async () => {
    try {
      const response = await axios.get(`${API_URL}/shop/admin/stats`);
      setStats(response.data);
    } catch (error) {
      console.error('Failed to fetch stats:', error);
    }
  };

  const updateOrderStatus = async (orderId, newStatus) => {
    try {
      await axios.put(`${API_URL}/shop/admin/orders/${orderId}/status?status=${newStatus}`);
      toast.success(`Order status updated to ${newStatus}`);
      fetchOrders();
      fetchStats();
    } catch (error) {
      toast.error('Failed to update order status');
    }
  };

  const getStatusColor = (status) => {
    const colors = {
      confirmed: 'bg-blue-500/20 text-blue-400',
      processing: 'bg-yellow-500/20 text-yellow-400',
      shipped: 'bg-purple-500/20 text-purple-400',
      delivered: 'bg-green-500/20 text-green-400',
      cancelled: 'bg-red-500/20 text-red-400',
      pending: 'bg-zinc-500/20 text-zinc-400'
    };
    return colors[status] || colors.pending;
  };

  const getPaymentColor = (status) => {
    const colors = { paid: 'text-green-400', pending: 'text-yellow-400', failed: 'text-red-400' };
    return colors[status] || 'text-zinc-400';
  };

  const statuses = ['pending', 'confirmed', 'processing', 'shipped', 'delivered', 'cancelled'];

  return (
    <div className="space-y-8">
      <div className="flex items-center justify-between">
        <h1 className="text-3xl font-bold">Orders</h1>
        <span className="text-sm text-zinc-400">{orders.length} orders</span>
      </div>

      {/* Stats */}
      {stats && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <div className="glass-card rounded-xl p-4">
            <Package className="w-5 h-5 text-[#D9F99D] mb-2" />
            <p className="text-2xl font-bold">{stats.total_orders}</p>
            <p className="text-zinc-400 text-sm">Total Orders</p>
          </div>
          <div className="glass-card rounded-xl p-4">
            <CheckCircle className="w-5 h-5 text-green-400 mb-2" />
            <p className="text-2xl font-bold">{stats.paid_orders}</p>
            <p className="text-zinc-400 text-sm">Paid Orders</p>
          </div>
          <div className="glass-card rounded-xl p-4">
            <Clock className="w-5 h-5 text-yellow-400 mb-2" />
            <p className="text-2xl font-bold">{stats.pending_orders}</p>
            <p className="text-zinc-400 text-sm">Pending</p>
          </div>
          <div className="glass-card rounded-xl p-4">
            <DollarSign className="w-5 h-5 text-[#D9F99D] mb-2" />
            <p className="text-2xl font-bold">${stats.total_revenue?.toFixed(2) || '0.00'}</p>
            <p className="text-zinc-400 text-sm">Revenue</p>
          </div>
        </div>
      )}

      {/* Filter Tabs */}
      <div className="flex gap-2 flex-wrap">
        {['all', ...statuses].map((status) => (
          <Button
            key={status}
            onClick={() => setFilter(status)}
            className={`rounded-full px-4 py-2 text-sm capitalize ${
              filter === status ? 'bg-[#D9F99D] text-black' : 'bg-white/5 text-zinc-400 hover:bg-white/10'
            }`}
          >
            {status}
          </Button>
        ))}
      </div>

      {/* Orders List */}
      <div className="space-y-4">
        {loading ? (
          <div className="text-center py-12 text-zinc-400">Loading orders...</div>
        ) : orders.length > 0 ? (
          orders.map((order) => (
            <div key={order.id} className="glass-card rounded-2xl overflow-hidden">
              <div 
                className="p-4 md:p-6 cursor-pointer hover:bg-white/5 transition-colors"
                onClick={() => setExpandedOrder(expandedOrder === order.id ? null : order.id)}
              >
                <div className="flex flex-wrap items-center justify-between gap-4">
                  <div>
                    <p className="font-bold text-lg">{order.order_number}</p>
                    <p className="text-sm text-zinc-400">
                      {new Date(order.created_at).toLocaleDateString()} • {order.items?.length || 0} items
                    </p>
                  </div>
                  
                  <div className="flex items-center gap-4">
                    <div className="text-right">
                      <p className={`text-sm font-medium capitalize ${getPaymentColor(order.payment_status)}`}>
                        {order.payment_status}
                      </p>
                      <p className="text-lg font-bold text-[#D9F99D]">${order.total?.toFixed(2)}</p>
                    </div>
                    <div className={`px-3 py-1 rounded-full text-sm font-medium capitalize ${getStatusColor(order.status)}`}>
                      {order.status}
                    </div>
                    {expandedOrder === order.id ? <ChevronUp className="w-5 h-5 text-zinc-400" /> : <ChevronDown className="w-5 h-5 text-zinc-400" />}
                  </div>
                </div>
              </div>

              {expandedOrder === order.id && (
                <div className="border-t border-white/10 p-4 md:p-6 space-y-6">
                  <div className="grid md:grid-cols-2 gap-6">
                    <div>
                      <h4 className="font-bold mb-3 flex items-center gap-2">
                        <Truck className="w-4 h-4 text-[#D9F99D]" />
                        Shipping Address
                      </h4>
                      <div className="text-sm text-zinc-400 space-y-1">
                        <p className="text-white font-medium">{order.shipping?.full_name}</p>
                        <p>{order.shipping?.address_line1}</p>
                        {order.shipping?.address_line2 && <p>{order.shipping?.address_line2}</p>}
                        <p>{order.shipping?.city}, {order.shipping?.state} {order.shipping?.zip_code}</p>
                        <p>{order.shipping?.email}</p>
                      </div>
                    </div>

                    <div>
                      <h4 className="font-bold mb-3 flex items-center gap-2">
                        <Package className="w-4 h-4 text-[#D9F99D]" />
                        Items
                      </h4>
                      <div className="space-y-2">
                        {order.items?.map((item, idx) => (
                          <div key={idx} className="flex justify-between text-sm">
                            <span className="text-zinc-400">{item.name} ({item.size}/{item.color}) x{item.quantity}</span>
                            <span>${item.total?.toFixed(2)}</span>
                          </div>
                        ))}
                        <div className="border-t border-white/10 pt-2 mt-2">
                          <div className="flex justify-between text-sm">
                            <span className="text-zinc-400">Subtotal</span>
                            <span>${order.subtotal?.toFixed(2)}</span>
                          </div>
                          <div className="flex justify-between text-sm">
                            <span className="text-zinc-400">Shipping</span>
                            <span>{order.shipping_cost === 0 ? 'FREE' : `$${order.shipping_cost?.toFixed(2)}`}</span>
                          </div>
                          <div className="flex justify-between font-bold mt-1">
                            <span>Total</span>
                            <span className="text-[#D9F99D]">${order.total?.toFixed(2)}</span>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>

                  <div className="flex items-center gap-4 pt-4 border-t border-white/10">
                    <span className="text-sm text-zinc-400">Update Status:</span>
                    <select
                      value={order.status}
                      onChange={(e) => updateOrderStatus(order.id, e.target.value)}
                      className="bg-black/40 border border-white/10 rounded-lg px-3 py-2 text-sm capitalize"
                    >
                      {statuses.map((status) => (
                        <option key={status} value={status}>{status}</option>
                      ))}
                    </select>
                  </div>
                </div>
              )}
            </div>
          ))
        ) : (
          <div className="glass-card rounded-2xl p-12 text-center">
            <Package className="w-12 h-12 text-zinc-600 mx-auto mb-4" />
            <h3 className="text-xl font-bold mb-2">No orders yet</h3>
            <p className="text-zinc-400">Orders will appear here when customers make purchases.</p>
          </div>
        )}
      </div>
    </div>
  );
};

export default AdminOrders;
